import { useEffect, useState, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { DataTableAdvanced, type DataTableColumn } from '@/components/ui/data-table-advanced'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  usersService,
  type User,
  type UserStats,
  getUserRoleLabel,
  getUserStatusLabel,
  getUserRoleColor,
  getUserStatusColor
} from '@/services/users'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Plus, MoreHorizontal, Mail, Calendar, Edit, Eye, UserCheck, UserX, Key, LogIn, Shield, X, Search, Loader2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { format } from 'date-fns'
import { useDrawer, DRAWER_PRESETS } from '@/components/ui/drawer'
import { UserFormDrawer, UserFormFooter } from '@/components/users/UserFormDrawer'
import { UserViewDrawer } from '@/components/users/UserViewDrawer'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { toast } from 'sonner'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

export const Users: React.FC = () => {
  const { openDrawer } = useDrawer()
  const { onMessage } = useDrawerMessaging('users-page')
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const [totalUsers, setTotalUsers] = useState(0)
  const [stats, setStats] = useState<UserStats | null>(null)
  const [pagination, setPagination] = useState({ page: 1, size: 10 })
  const hasMounted = useRef(false)

  // Pending confirmation for destructive actions
  const [confirmAction, setConfirmAction] = useState<
    | { type: 'suspend' | 'activate' | 'reset-password'; user: User }
    | null
  >(null)
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  
  // Filter state
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Main fetch function
  const fetchUsers = useCallback(async (search?: string, role?: string, status?: string) => {
    try {
      setLoading(true)
      const filters: Record<string, unknown> = {
        page: pagination.page,
        size: pagination.size,
      }
      
      // Add search if provided
      if (search && search.trim()) {
        filters.search = search.trim()
      }
      
      // Add role filter if not "all"
      if (role && role !== 'all') {
        const roleToId: Record<string, number> = {
          'super_admin': 42,
          'admin': 7,
          'manager': 2,
          'sales_rep': 1,
          'site_surveyor': 21,
          'user': 12,
        }
        const roleId = roleToId[role]
        if (roleId) {
          filters.role_id = roleId
        }
      }
      
      // Add status filter if not "all"
      if (status && status !== 'all') {
        filters.is_active = status === 'active'
      }
      
      console.log('[Users] Fetching with filters:', filters)
      const response = await usersService.list(filters)
      setUsers(response.items)
      setTotalUsers(response.total)
      // Refresh aggregate stats alongside the list (non-blocking)
      usersService
        .stats()
        .then(setStats)
        .catch((statsErr) => console.error('Failed to fetch user stats:', statsErr))
    } catch (err) {
      console.error('Failed to fetch users:', err)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
      setIsSearching(false)
    }
  }, [pagination])

  // Combined effect for initial load and pagination changes
  useEffect(() => {
    if (!hasMounted.current) {
      // Initial load
      hasMounted.current = true
      fetchUsers('', 'all', 'all')
    } else {
      // Pagination changed after mount
      fetchUsers(searchQuery, roleFilter, statusFilter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.size])

  // Listen for refresh messages from drawers  
  useEffect(() => {
    return onMessage((message) => {
      if (message.action === 'refresh') {
        fetchUsers(searchQuery, roleFilter, statusFilter)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMessage])

  // Handle search with debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setIsSearching(true)
    
    // Clear existing timer
    if (searchTimer) {
      clearTimeout(searchTimer)
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      console.log('[Users] Search timer fired for:', value)
      setPagination({ page: 1, size: 10 }) // Reset to page 1
      fetchUsers(value, roleFilter, statusFilter)
    }, 500)
    
    setSearchTimer(timer)
  }

  // Handle role filter change
  const handleRoleChange = (value: string) => {
    console.log('[Users] Role changed to:', value)
    setRoleFilter(value)
    setPagination({ page: 1, size: 10 }) // Reset to page 1
    fetchUsers(searchQuery, value, statusFilter)
  }

  // Handle status filter change
  const handleStatusChange = (value: string) => {
    console.log('[Users] Status changed to:', value)
    setStatusFilter(value)
    setPagination({ page: 1, size: 10 }) // Reset to page 1
    fetchUsers(searchQuery, roleFilter, value)
  }

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('')
    setRoleFilter('all')
    setStatusFilter('all')
    setPagination({ page: 1, size: 10 })
    fetchUsers('', 'all', 'all')
  }

  const columns: DataTableColumn<User>[] = [
    {
      accessorKey: 'user',
      header: 'User',
      cell: ({ row }) => {
        const user = row.original
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.avatar_url} alt={user.full_name} />
              <AvatarFallback>
                {user.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{user.full_name}</div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Mail className="h-3 w-3" />
                {user.email}
              </div>
            </div>
          </div>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => (
        <Badge variant={getUserRoleColor(row.original.role)}>
          <Shield className="mr-1 h-3 w-3" />
          {getUserRoleLabel(row.original.role)}
        </Badge>
      ),
      filterFn: 'select',
    },
    {
      accessorKey: 'tenant_name',
      header: 'Tenant',
      cell: ({ row }) => row.original.tenant_name || '-',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getUserStatusColor(row.original.status)}>
          {getUserStatusLabel(row.original.status)}
        </Badge>
      ),
      filterFn: 'select',
    },
    {
      accessorKey: 'email_verified',
      header: 'Verified',
      cell: ({ row }) => (
        row.original.email_verified ? (
          <Badge variant="outline" className="text-primary">
            <UserCheck className="mr-1 h-3 w-3" />
            Verified
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            <UserX className="mr-1 h-3 w-3" />
            Unverified
          </Badge>
        )
      ),
    },
    {
      accessorKey: 'last_login',
      header: 'Last Login',
      cell: ({ row }) => (
        row.original.last_login ? (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <LogIn className="h-3 w-3" />
            {format(new Date(row.original.last_login), 'MMM d, yyyy')}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">Never</span>
        )
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Calendar className="h-3 w-3" />
          {format(new Date(row.original.created_at), 'MMM d, yyyy')}
        </div>
      ),
    },
  ]

  // Note: filters are now handled by custom UI above the table

  const handleAddUser = () => {
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: 'user-form-new',
      title: 'Create New User',
      content: <UserFormDrawer onSuccess={() => fetchUsers(searchQuery, roleFilter, statusFilter)} />,
      footer: <UserFormFooter isEditMode={false} drawerId="user-form-new" />,
      size: 'lg',
    })
  }

  const handleEditUser = (user: User) => {
    const drawerId = `user-form-${user.id}`
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: drawerId,
      title: `Edit User: ${user.full_name}`,
      content: <UserFormDrawer userId={user.id} onSuccess={() => fetchUsers(searchQuery, roleFilter, statusFilter)} />,
      footer: <UserFormFooter isEditMode={true} drawerId={drawerId} userId={user.id} />,
      size: 'lg',
    })
  }

  const handleViewUser = (user: User) => {
    openDrawer({
      ...DRAWER_PRESETS.details,
      id: `user-view-${user.id}`,
      title: `User Details: ${user.full_name}`,
      content: <UserViewDrawer userId={user.id} />,
      size: 'lg',
    })
  }

  const handleSuspendUser = (user: User) => {
    setConfirmAction({
      type: user.status === 'active' ? 'suspend' : 'activate',
      user,
    })
  }

  const handleResetPassword = (user: User) => {
    setConfirmAction({ type: 'reset-password', user })
  }

  const handleConfirmAction = async () => {
    if (!confirmAction) return
    const { type, user } = confirmAction
    setConfirmAction(null)

    try {
      if (type === 'suspend') {
        await usersService.suspend(user.id)
        toast.success(`User ${user.full_name} has been suspended`)
        fetchUsers(searchQuery, roleFilter, statusFilter)
      } else if (type === 'activate') {
        await usersService.activate(user.id)
        toast.success(`User ${user.full_name} has been activated`)
        fetchUsers(searchQuery, roleFilter, statusFilter)
      } else {
        await usersService.resetPassword(user.id)
        toast.success(`Password reset link sent to ${user.email}`)
      }
    } catch (error) {
      console.error(`Failed to ${type} user:`, error)
      toast.error(
        type === 'reset-password'
          ? 'Failed to reset password'
          : 'Failed to update user status'
      )
    }
  }

  const handleImpersonate = async (user: User) => {
    try {
      const response = await usersService.impersonate(user.id)
      
      // Store the impersonation token
      localStorage.setItem('impersonation_token', response.token)
      
      // Redirect to joules-web with the token
      const joulesWebUrl = import.meta.env.VITE_JOULES_WEB_URL || 'http://localhost:5174'
      window.open(`${joulesWebUrl}/impersonate?token=${response.token}`, '_blank')
      
      toast.success(`Impersonating ${user.full_name}`)
    } catch (error) {
      console.error('Failed to impersonate user:', error)
      toast.error('Failed to impersonate user')
    }
  }

  const handleExport = async () => {
    try {
      const blob = await usersService.exportUsers()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `users-${format(new Date(), 'yyyy-MM-dd')}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Users exported successfully')
    } catch (error) {
      console.error('Failed to export users:', error)
      toast.error('Failed to export users')
    }
  }

  const renderActions = (user: User) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleViewUser(user)}>
          <Eye className="mr-2 h-4 w-4" />
          View details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleEditUser(user)}>
          <Edit className="mr-2 h-4 w-4" />
          Edit user
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleResetPassword(user)}>
          <Key className="mr-2 h-4 w-4" />
          Reset password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {user.role !== 'super_admin' && (
          <DropdownMenuItem onClick={() => handleImpersonate(user)}>
            <LogIn className="mr-2 h-4 w-4" />
            Impersonate
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className={user.status === 'active' ? 'text-destructive' : 'text-primary'}
          onClick={() => handleSuspendUser(user)}
        >
          {user.status === 'active' ? (
            <>
              <UserX className="mr-2 h-4 w-4" />
              Suspend user
            </>
          ) : (
            <>
              <UserCheck className="mr-2 h-4 w-4" />
              Activate user
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Users</h1>
          <p className="text-muted-foreground mt-1">
            Manage platform users and permissions
            {searchQuery && (
              <span className="ml-2">
                • Found {totalUsers} result{totalUsers !== 1 ? 's' : ''} for "{searchQuery}"
              </span>
            )}
          </p>
        </div>
        <Button onClick={handleAddUser}>
          <Plus className="mr-2 h-4 w-4" />
          Add New User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Users</p>
              <p className="text-2xl font-bold">{stats?.total ?? totalUsers}</p>
            </div>
            <Shield className="h-8 w-8 text-muted-foreground opacity-50" />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active</p>
              <p className="text-2xl font-bold">{stats?.active ?? '—'}</p>
            </div>
            <Badge variant="default" className="h-6">Active</Badge>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Verified</p>
              <p className="text-2xl font-bold">{stats?.verified ?? '—'}</p>
            </div>
            <UserCheck className="h-8 w-8 text-primary opacity-50" />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Admins</p>
              <p className="text-2xl font-bold">{stats?.admins ?? '—'}</p>
            </div>
            <Badge variant="destructive" className="h-6">Admin</Badge>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          {/* Search or Loading Icon */}
          {isSearching ? (
            <Loader2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          ) : (
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          )}
          
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => {
              // Clear search on Escape key
              if (e.key === 'Escape') {
                handleSearchChange('')
              }
            }}
            className="pl-9 pr-9"
          />
          
          {/* Clear button */}
          {searchQuery && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 hover:bg-accent"
              title="Clear search"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          
          {/* Search status indicator */}
          {isSearching && (
            <span className="absolute -bottom-5 left-0 text-xs text-muted-foreground">
              Searching...
            </span>
          )}
        </div>
        
        <Select
          value={roleFilter}
          onValueChange={handleRoleChange}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="sales_rep">Sales Rep</SelectItem>
            <SelectItem value="site_surveyor">Site Surveyor</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>
        
        <Select
          value={statusFilter}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        
        {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            disabled={isSearching}
          >
            Clear filters
            <X className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Data Table */}
      <div>
        <DataTableAdvanced
          columns={columns}
          data={users}
          loading={loading}
          searchable={false}
          searchPlaceholder="Search users..."
          searchKeys={['full_name', 'email', 'tenant_name']}
          filters={[]}  
          showColumnVisibility={true}
          showPagination={true}
          showSelection={true}
          serverSidePagination={true}
          totalRows={totalUsers}
          currentPage={pagination.page}
          pageSize={pagination.size}
          pageSizeOptions={[10, 20, 30, 50]}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(size) => setPagination({ page: 1, size })}
          onRowSelect={setSelectedUsers}
          onExport={handleExport}
          actions={renderActions}
          emptyMessage="No users found."
          toolbar={
            selectedUsers.length > 0 && (
              <Badge variant="secondary">
                {selectedUsers.length} selected
              </Badge>
            )
          }
        />
      </div>

      {/* Confirmation for destructive actions */}
      <ConfirmDialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null)
        }}
        title={
          confirmAction?.type === 'suspend'
            ? 'Suspend user?'
            : confirmAction?.type === 'activate'
              ? 'Activate user?'
              : 'Send password reset?'
        }
        description={
          confirmAction?.type === 'suspend'
            ? `${confirmAction.user.full_name} (${confirmAction.user.email}) will no longer be able to sign in.`
            : confirmAction?.type === 'activate'
              ? `${confirmAction?.user.full_name} (${confirmAction?.user.email}) will regain access to their account.`
              : `A password reset link will be emailed to ${confirmAction?.user.email}.`
        }
        confirmLabel={
          confirmAction?.type === 'suspend'
            ? 'Suspend'
            : confirmAction?.type === 'activate'
              ? 'Activate'
              : 'Send reset link'
        }
        destructive={confirmAction?.type === 'suspend'}
        onConfirm={handleConfirmAction}
      />
    </div>
  )
}